import { useFormik } from "formik";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { closeModal } from "../../app/features/slices/modalSlice";
import { createPasswordThunk } from "../../app/features/thunks/customerThunks";
import { useAuth } from "../../contexts/AuthContext";
import PasswordField from "../UniversalComponent/PasswordField";

const CreatePasswordForm = ({ enforceChange = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user, handleSuccessfulPasswordChange } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const strongPasswordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).+$/;
  const validationSchema = Yup.object({
    oldPassword: enforceChange
      ? Yup.string().notRequired()
      : Yup.string()
        .required(t("changepassword.old_password_required"))
        .min(6, t("changepassword.password_min_length")),

    newPassword: Yup.string()
      .required(t("changepassword.new_password_required"))
      .min(6, t("changepassword.password_min_length"))
      .matches(strongPasswordRegex, t("changepassword.password_strength_error"))
      .notOneOf(
        enforceChange ? [] : [Yup.ref("oldPassword")],
        t("changepassword.new_password_diff")
      ),

    confirmPassword: Yup.string()
      .required(t("changepassword.confirm_password_required"))
      .oneOf(
        [Yup.ref("newPassword")],
        t("changepassword.passwords_must_match")
      ),
  });

  const formik = useFormik({
    initialValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: (values) => {
      setIsLoading(true);
      const payload = {
        id: user?.id || 0,
        oldPassword: enforceChange ? "" : values.oldPassword,
        newPassword: values.newPassword,
        isCustomer: user?.role?.toLowerCase() === "customer",
      };

      dispatch(createPasswordThunk(payload))
        .then((action) => {
          if (action.payload?.message) {
            toast.success(t("changepassword.password_changed_success"));
            if (enforceChange) {
              handleSuccessfulPasswordChange();
            } else {
              dispatch(closeModal());
            }
          } else {
            toast.error(
              action.payload?.message ||
              t("changepassword.password_change_failed")
            );
          }
        })
        .catch((error) => {
          toast.error(
            error.message || t("changepassword.password_change_failed")
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
  });

  return (
    <form
      onSubmit={formik.handleSubmit}
      className="max-w-2xl mx-auto bg-white rounded-2xl p-6 space-y-4"
    >
      {!enforceChange && (
        <PasswordField
          label={t("changepassword.old_password")}
          name="oldPassword"
          value={formik.values.oldPassword}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.oldPassword && formik.errors.oldPassword
              ? formik.errors.oldPassword
              : null
          }
        />
      )}

      <PasswordField
        label={t("changepassword.new_password")}
        name="newPassword"
        value={formik.values.newPassword}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={
          formik.touched.newPassword && formik.errors.newPassword
            ? formik.errors.newPassword
            : null
        }
      />

      <PasswordField
        label={t("changepassword.confirm_password")}
        name="confirmPassword"
        value={formik.values.confirmPassword}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={
          formik.touched.confirmPassword && formik.errors.confirmPassword
            ? formik.errors.confirmPassword
            : null
        }
      />

      <div className="flex justify-center pt-4">
        <button
          type="submit"
          disabled={isLoading}
          className="flex items-center gap-2 px-5 py-2.5 btn-jk-yellow rounded-xl text-sm font-semibold shadow-sm hover:shadow-md active:scale-[0.97] transition-all duration-200"
        >
          {isLoading
            ? t("changepassword.changing_password")
            : t("changepassword.change_password")}
        </button>
      </div>
    </form>
  );
};

export default CreatePasswordForm;
